import {IClientOptionsSettings} from "./client-options-settings.model";

export type FunctionType = 'Logic App' | 'UI' | 'Products' | 'ProductCard' | 'Email' | 'Workflow';

export interface IWorkflowFunctionDisplayMetadata {
    toolName: string;
    description: string;
}

export interface IWorkflowFunctionWaitMetadata {
    executionMode: string;
    waitTimeoutMs: number;
}

export interface IWorkflowFunctionBindingAvailability {
    runtimeReady: boolean;
    missing: boolean;
    blockingReasons: string[];
    message: string;
}

export interface IWorkflowFunctionBinding {
    operationId: string;
    workflowId: string;
    workflowVersion: string;
    specHash: string;
    inputSchema: string;
    display: IWorkflowFunctionDisplayMetadata;
    wait: IWorkflowFunctionWaitMetadata;
    availability?: IWorkflowFunctionBindingAvailability | null;
}

export interface IFunctionArgumentLookup {
    sourceArgument: string;
    targetArgument: string;
    values: Record<string, string>;
    removeSourceArgument: boolean;
    required?: boolean;
    scopeArgument?: string;
    scopedValues?: Record<string, Record<string, string>>;
}

export interface IConfiguredFunctionRequestPolicy {
    method: 'POST';
    argumentPlacement: 'json_body';
}

export interface IFormSubmissionFunctionBinding {
    functionId: string;
    functionVersion: number;
    toolName: string;
    functionType: 'Logic App';
    contractVersion: string;
    inputSchema: string;
}

export interface IHubSpotFormPresentation {
    contractVersion: 'hubspot-form-presentation.v1';
    region: 'na1' | 'eu1';
    portalId: string;
    formId: string;
}

export interface IEmailFunctionRecipient {
    key: string;
    label: string;
    email: string;
    description: string;
}

export interface IFunctionOptions extends IClientOptionsSettings {
    includeConversationImages?: boolean;
    etag?: string;
    contractVersion?: string;
    version?: number;
    parameters: string;  // JSON string representing a dictionary of parameters
    outputSchema?: string;
    authority?: 'configured_provider' | 'shine_runtime' | 'delegated_downstream';
    execution?: {timeoutMs: number; maximumAttempts: number; initialBackoffMs: number; maximumConcurrency: number};
    request?: IConfiguredFunctionRequestPolicy;
    authentication?: {mode: 'none' | 'bearer_reference' | 'logic_app_sas' | 'connect_signed'; referenceConfigured: boolean};
    idempotency?: {mode: 'none' | 'provider_call_id'; headerName?: string};
    type: FunctionType;
    toolName?: string;
    endpoint: string;
    endpointConfigured?: boolean;
    endpointCredentialConfigured?: boolean;
    waitMessage: string;
    bearerKey: string;
    assistantMediaHostAllowlist?: string[];
    argumentLookups?: IFunctionArgumentLookup[];
    emailRecipients?: IEmailFunctionRecipient[];
    emailSubjectTemplate?: string;
    emailBodyTemplate?: string;
    emailRequireConsentToContact?: boolean;
    emailIncludeTranscript?: boolean;
    workflowBinding?: IWorkflowFunctionBinding | null;
    formSubmissionBinding?: IFormSubmissionFunctionBinding | null;
    hubSpotFormPresentation?: IHubSpotFormPresentation | null;
}

export type SyntheticFunctionTestFixture =
    'success' | 'empty' | 'malformed' | 'delayed' | 'timeout' | 'retryable' | 'permanent' | 'unauthorized';

export interface IFunctionTestRequest {
    mode: 'synthetic';
    fixture: SyntheticFunctionTestFixture;
    arguments: Record<string, unknown>;
}

export interface IFunctionPatchChange { field: string; oldValue: unknown; newValue: unknown; }
export interface IFunctionPatchRequest { etag: string; changes: IFunctionPatchChange[]; }
export interface IFunctionAssistantMediaHostAllowlistUpdateRequest {
    etag: string;
    assistantMediaHostAllowlist: string[];
}
export interface IFunctionAssistantMediaHostAllowlistUpdateResponse {
    id: string;
    etag: string;
    assistantMediaHostAllowlist: string[];
    updatedDate: Date;
}
export interface IFunctionBearerCredentialUpdateRequest {
    etag: string;
    endpoint?: string;
    bearerKey?: string;
    bearerKeyRef?: string;
    clear?: boolean;
}

export type ConfiguredFunctionSelection = 'Unselected' | 'Explicit' | 'ImplicitProduct' | number;
export type ConfiguredFunctionConflictKind =
    'DuplicateNormalizedToolName' | 'DescriptionOverlap' | 'SchemaPropertyOverlap' | number;

export interface IConfiguredFunctionConflict {
    kind: ConfiguredFunctionConflictKind;
    evidence: string;
    otherFunctionIds: string[];
}

export interface IConfiguredFunctionManifestEntry {
    functionId: string;
    version: number;
    contractVersion: string;
    functionType: string;
    enabled: boolean;
    selection: ConfiguredFunctionSelection;
    baseToolName: string;
    resolvedToolName: string;
    providerVisibleDescription: string;
    providerVisibleInputSchema: string;
    normalReachable: boolean;
    realtimeReachable: boolean;
    hiddenReasons: string[];
    unreachableReasons: string[];
    migrationReasons: string[];
    conflicts: IConfiguredFunctionConflict[];
}

export interface IConfiguredFunctionManifest {
    entries: IConfiguredFunctionManifestEntry[];
}

export function isWorkflowFunctionBindingReady(binding?: IWorkflowFunctionBinding | null): boolean {
    return !!binding?.availability?.runtimeReady
        && !binding.availability.missing
        && binding.availability.blockingReasons.length === 0;
}

export function workflowFunctionBindingUnavailableMessage(binding?: IWorkflowFunctionBinding | null): string {
    if (!binding) {
        return 'Select a ready Connect workflow before continuing.';
    }

    return binding.availability?.message || 'Refresh the Connect workflow inventory and select a ready workflow before continuing.';
}
